import { postProviderJson, providerError } from './providerHttp';
import { streamProviderSse, canStream } from '../providerStream';
import {
  createTurnAssembly,
  pushEvent,
  unfinishedToolCalls,
} from '../anthropicStreamAssembly';

/**
 * Anthropic Messages API adapter (BYO key).
 *
 * The panel's message history IS Anthropic-shaped (the internal lingua
 * franca — see toolSchemas.js), so this adapter passes messages and tools
 * through untouched. What it adds:
 *
 *  - prompt-cache breakpoints on the last static system block and the last
 *    tool, exactly as the retired server proxy did — the stable prefix
 *    (tools + system) is by far the biggest token cost per turn and the
 *    user is now paying their own bill;
 *  - the anthropic-dangerous-direct-browser-access header, harmless from
 *    the Rust client and required if this ever runs in a plain webview.
 *
 * Two transports. `send` buffers the whole reply and emits its text as one
 * delta; `sendStreaming` opens an SSE stream and emits text as it arrives.
 * `supportsStreaming()` decides — it needs the Tauri command, so the browser
 * build always takes the buffered path.
 *
 * Both produce the same callbacks and the same { stopReason }, so nothing
 * upstream of the adapter knows which ran.
 */

const API_URL = 'https://api.anthropic.com/v1/messages';
const API_VERSION = '2023-06-01';
const MAX_TOKENS = 8096;
const CACHE_CONTROL = { type: 'ephemeral' };

function headers(apiKey) {
  return {
    'x-api-key': apiKey,
    'anthropic-version': API_VERSION,
    'anthropic-dangerous-direct-browser-access': 'true',
  };
}

// Cache the whole static tools array by marking the LAST tool. INVARIANT
// (ported from the server): dynamically-appended tools must come AFTER this
// list so they never invalidate the cached prefix.
function toolsWithCacheBreakpoint(tools) {
  if (!tools || tools.length === 0) return [];
  const last = tools[tools.length - 1];
  return [...tools.slice(0, -1), { ...last, cache_control: CACHE_CONTROL }];
}

function systemBlocks(system, reference) {
  const blocks = [{ type: 'text', text: system }];
  if (reference) blocks.push({ type: 'text', text: reference });
  blocks[blocks.length - 1] = { ...blocks[blocks.length - 1], cache_control: CACHE_CONTROL };
  return blocks;
}

/**
 * One non-streamed model turn.
 * `system` is the prompt string; `reference` the static cached reference
 * (optional). Emits onTextDelta / onToolUse / onUsage, returns { stopReason }.
 */
export async function send({ apiKey, model, system, reference, tools, messages, onTextDelta, onToolUse, onUsage }) {
  const body = {
    model,
    max_tokens: MAX_TOKENS,
    system: systemBlocks(system, reference),
    tools: toolsWithCacheBreakpoint(tools),
    messages,
  };

  const { status, json } = await postProviderJson(API_URL, headers(apiKey), body);
  if (status >= 400 || !json) {
    throw providerError('Anthropic', status, json?.error?.message);
  }

  if (json.usage) onUsage?.(json.usage);
  for (const block of json.content || []) {
    if (block.type === 'text' && block.text) onTextDelta?.(block.text);
    if (block.type === 'tool_use') {
      onToolUse?.({ id: block.id, name: block.name, input: block.input || {} });
    }
  }
  return { stopReason: json.stop_reason || 'end_turn' };
}

/** Whether this adapter can stream in the current runtime. */
export function supportsStreaming() {
  return canStream();
}

/**
 * Stream a turn, emitting text as it arrives and tool calls once complete.
 *
 * `onStart` hands back a cancel function that aborts the Rust task — not just
 * the listeners. On the user's own API key, a "stopped" turn that keeps
 * generating is one they still pay for.
 */
export async function sendStreaming({
  apiKey, model, system, reference, tools, messages,
  onTextDelta, onToolUse, onUsage, onStart,
}) {
  const body = {
    model,
    max_tokens: MAX_TOKENS,
    system: systemBlocks(system, reference),
    tools: toolsWithCacheBreakpoint(tools),
    messages,
    stream: true,
  };

  const state = createTurnAssembly();
  let streamError = null;

  await streamProviderSse(API_URL, headers(apiKey), body, (payload) => {
    const out = pushEvent(state, payload);
    if (out.error) {
      streamError = out.error;
      return;
    }
    if (out.text) onTextDelta?.(out.text);
    // Emitted only on content_block_stop — see anthropicStreamAssembly.js.
    if (out.toolUse) onToolUse?.(out.toolUse);
    if (out.usage) onUsage?.(out.usage);
  }, { onStart });

  if (streamError) throw providerError('Anthropic', 0, streamError);

  // A stream cut mid-tool-call leaves its arguments half-written. Reporting
  // that is the honest outcome; running the call is the bug this guards.
  const unfinished = unfinishedToolCalls(state);
  if (unfinished.length) {
    throw new Error(
      `The response was cut off while calling ${unfinished.map((t) => t.name).join(', ')}. Nothing was run — ask again.`
    );
  }

  return { stopReason: state.stopReason || 'end_turn' };
}

/** Minimal call proving the key works. Throws with the provider's message when it doesn't. */
export async function testKey({ apiKey, model }) {
  const body = {
    model,
    max_tokens: 1,
    messages: [{ role: 'user', content: 'ping' }],
  };
  const { status, json } = await postProviderJson(API_URL, headers(apiKey), body, { timeoutSecs: 30 });
  if (status >= 400 || !json) {
    throw providerError('Anthropic', status, json?.error?.message);
  }
  return true;
}
